/**
 * THE STATEMENT OF APPLICABILITY EDITOR — ISO/IEC 27001 clause 6.1.3(d).
 *
 * Ninety-three rows, and only the customer can fill them in. That number is the
 * whole design problem: an SoA screen that takes an afternoon is an SoA that
 * never gets written, and an organisation without an SoA does not get
 * certified. So everything here exists to cut the time to a decision —
 *
 *   * bulk selection with a sticky action bar, so a theme can be decided at
 *     once and then refined;
 *   * keyboard decisions on the focused row (A / E / U), because the realistic
 *     way through 93 rows is arrow-down-A-arrow-down-A;
 *   * justification saved on blur, never behind a modal;
 *   * an "undecided only" filter, which is the view the work is actually done
 *     in.
 *
 * ---------------------------------------------------------------------------
 *  WHAT THIS SCREEN WILL NOT DO, AND WHY IT IS THE IMPORTANT PART
 * ---------------------------------------------------------------------------
 * There is no "mark all applicable" default, no pre-selected radio button, and
 * no state where a control drifts into APPLICABLE without a person choosing it.
 * A control nobody has decided is rendered as "Not yet decided" in a colour
 * that reads as outstanding work, and it exports that way too.
 *
 * The temptation is obvious — default everything to applicable, the screen
 * looks 100% complete, the customer edits the handful they want excluded. It is
 * also the one thing that would make the exported document a forgery: the SoA
 * is signed and handed to a certification body as the organisation's own
 * statement, so a decision the software took on their behalf appears over their
 * signature. "Incomplete" is a problem the customer can see. "Wrong, and
 * attributed to you" is not.
 *
 * Implementation status is different and is derived where the system honestly
 * knows it — from a control evaluation, or from a satisfied cross-framework
 * control. Every derived value is labelled, names its source, and is overridden
 * the moment a person states one.
 */

import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  SOA_APPLICABILITY,
  SOA_IMPLEMENTATION_STATUSES,
  SOA_EXCLUSION_GROUNDS,
  SOA_EXCLUSION_JUSTIFICATION,
  SOA_JUSTIFICATION_REASONS,
  type SoaApplicability,
  type SoaExclusionGround,
  type SoaImplementationStatus,
  type SoaJustificationReason,
} from '@opencomply/shared';
import { api } from '../lib/api';
import type {
  SoaDocument, SoaRow, SoaDecisionPatch as DecisionPatch,
} from '../lib/soaTypes';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { SOA_CONTROL_GUIDANCE } from '@opencomply/shared';
import {
  Card, Button, Pill, Skeleton, ErrorNotice, EmptyState, Field, type Tone,
} from '../components/ui';

// ---------------------------------------------------------------------------
//  Strings — inline English defaults, the EVIDENCE_STRINGS pattern.
//
//  Nothing here is added to en.json alone, because the parity guard would fail
//  the build and adding untranslated Greek would be worse. The keys are
//  reported to the maintainer so el.json can be filled in a single pass.
// ---------------------------------------------------------------------------

export const SOA_STRINGS = {
  'soa.title': 'Statement of Applicability',
  'soa.subtitle':
    'ISO/IEC 27001 clause 6.1.3(d). Every Annex A control, whether it applies to you, and why. This is the document a certification auditor asks for first.',
  'soa.loadFailed': 'Could not load the Statement of Applicability.',
  'soa.empty': 'ISO/IEC 27001 Annex A has not been seeded yet.',
  'soa.emptyHint': 'Run the database seed to populate the control set.',
  'soa.scope': 'ISMS scope statement',
  'soa.scopeHint':
    'What the management system covers — sites, services, people and systems — and anything carved out. Every exclusion below is justified against this, and the exported document prints it.',
  'soa.scopeMissing':
    'No scope statement has been recorded. The exported document says so rather than assuming the scope is the whole organisation.',
  'soa.scopePlaceholder':
    'e.g. The provision of renewable energy generation and grid services from the Athens head office and the Thessaloniki operations centre, including the supporting IT infrastructure.',
  'soa.scopeSave': 'Save scope',
  'soa.applicable': 'Applicable',
  'soa.excluded': 'Excluded',
  'soa.undecided': 'Not yet decided',
  'soa.implemented': 'Implemented',
  'soa.undecidedWarning_one':
    '{{count}} control has no applicability decision. Until it does, the exported document is not a complete Statement of Applicability — and it says so.',
  'soa.undecidedWarning_other':
    '{{count}} controls have no applicability decision. Until they do, the exported document is not a complete Statement of Applicability — and it says so.',
  'soa.noJustification_one':
    '{{count}} decided control has no written justification. An exclusion without one is the most commonly raised finding against this document.',
  'soa.noJustification_other':
    '{{count}} decided controls have no written justification. An exclusion without one is the most commonly raised finding against this document.',
  'soa.revisedAt': 'Decisions last changed {{date}}',
  'soa.neverRevised': 'No decision has been recorded yet.',

  // Filters
  'soa.filterTheme': 'Theme',
  'soa.filterAllThemes': 'All themes',
  'soa.filterApplicability': 'Decision',
  'soa.filterAllDecisions': 'All decisions',
  'soa.search': 'Search',
  'soa.searchPlaceholder': 'Control reference or title',
  'soa.showing': 'Showing {{shown}} of {{total}}',
  'soa.undecidedOnly': 'Undecided only',

  // Bulk
  'soa.selectAll': 'Select all shown',
  'soa.clearSelection': 'Clear selection',
  'soa.selected_one': '{{count}} control selected',
  'soa.selected_other': '{{count}} controls selected',
  'soa.bulkApplicable': 'Mark applicable',
  'soa.bulkUndecided': 'Clear decision',
  'soa.bulkJustification': 'Justification to apply to all selected (optional)',
  'soa.bulkReasons': 'Reasons to apply',

  // Row
  'soa.applicability.APPLICABLE': 'Applicable',
  'soa.applicability.EXCLUDED': 'Excluded',
  'soa.applicability.UNDECIDED': 'Not yet decided',
  'soa.justification': 'Justification',
  'soa.justificationApplicable':
    'Why this control is included — the risk it treats, or the legal, regulatory or contractual requirement behind it.',
  'soa.justificationExcluded':
    'Why this control does not apply to the scope above. An auditor reads these hardest.',
  'soa.justificationUndecided':
    'Record a decision first. Nothing is written into the document until you do.',
  /*
    The example is a PLACEHOLDER and is prefixed so it cannot be mistaken for a
    value somebody entered. It disappears on the first keystroke and is never
    submitted; see the note above SOA_CONTROL_GUIDANCE for why that matters.
  */
  'soa.justificationExample': 'For example — {{example}}',
  /*
    Shown when a control the product has no honest exclusion example for is
    excluded anyway. It teaches the shape of an exclusion without putting words
    in the customer's mouth for a control we believe applies to everyone.
  */
  'soa.justificationExcludedShape':
    'For example — name what in the scope above puts this control outside it, and say what would bring it back in.',
  'soa.justificationMissing': 'No justification recorded',
  'soa.excludeReason': 'Why does it not apply?',
  'soa.excludeReasonOther': 'Say why, in your own words',
  'soa.excludeConfirm': 'Record the exclusion',
  'soa.excludeCancel': 'Cancel',
  'soa.ground.NO_IN_HOUSE_DEVELOPMENT': 'We write no software of our own',
  'soa.ground.NO_OUTSOURCED_DEVELOPMENT': 'We do not have software built for us',
  'soa.ground.NO_TEST_ENVIRONMENTS': 'We have no test or development environments',
  'soa.ground.NO_OWN_PREMISES': 'We occupy no premises of our own',
  'soa.ground.NO_ON_PREMISES_INFRASTRUCTURE': 'We run no IT equipment on our own premises',
  'soa.ground.NO_REMOVABLE_MEDIA': 'We do not use removable media',
  'soa.ground.NO_REMOTE_WORKING': 'Nobody works away from our premises',
  'soa.ground.NO_CLOUD_SERVICES': 'We use no cloud services',
  'soa.ground.NO_SUPPLIERS': 'We use no external suppliers',
  'soa.ground.NO_PAYMENT_CARD_DATA': 'We handle no payment card data',
  'soa.ground.NO_INDUSTRIAL_CONTROL_SYSTEMS': 'We have no industrial control or OT systems',
  'soa.ground.NO_PERSONAL_DATA_BEYOND_EMPLOYEES': 'We hold no personal data beyond employee records',
  'soa.ground.OUTSIDE_SCOPE_BOUNDARY': 'It falls outside our ISMS scope',
  'soa.ground.OTHER': 'Something else',
  'soa.about': 'In plain terms: {{about}}',
  'soa.suggestion': 'Suggested applicable — {{basis}}',
  'soa.acceptSuggestion': 'Confirm applicable',
  'soa.bulkExcludeRefused':
    'Exclusions are recorded one at a time, with a reason each. A sweep of exclusions is the first thing an auditor looks for, so bulk actions can only confirm applicability.',
  'soa.reasons': 'Driven by',
  'soa.reason.RISK_ASSESSMENT': 'Risk assessment',
  'soa.reason.LEGAL': 'Legal requirement',
  'soa.reason.REGULATORY': 'Regulatory requirement',
  'soa.reason.CONTRACTUAL': 'Contractual obligation',
  'soa.reason.BUSINESS_REQUIREMENT': 'Business requirement',
  'soa.reason.BEST_PRACTICE': 'Industry good practice',
  'soa.implementation': 'Implementation',
  'soa.implementation.IMPLEMENTED': 'Implemented',
  'soa.implementation.PARTIALLY_IMPLEMENTED': 'Partly implemented',
  'soa.implementation.PLANNED': 'Planned',
  'soa.implementation.NOT_IMPLEMENTED': 'Not implemented',
  'soa.implementationNotStated': 'Not stated',
  'soa.implementationUseDerived': 'Use the derived status',
  'soa.derived': 'Derived',
  'soa.derivedHint':
    'Read from records already in the system. Choosing a status here replaces it with your own.',
  'soa.evidence': 'Demonstrated by',
  'soa.noEvidence': 'Nothing attached',
  'soa.decidedBy': 'Decided {{date}} by {{name}}',
  'soa.decidedOn': 'Decided {{date}}',

  // Keyboard
  'soa.keyboardHint':
    'Keyboard: ↑ ↓ move between controls, A applicable, U clear the decision, Space select. Excluding asks for a reason, so it has no shortcut.',
} as const;

export type SoaKey = keyof typeof SOA_STRINGS;

const PLURAL_KEYS = [
  'soa.undecidedWarning',
  'soa.noJustification',
  'soa.selected',
] as const;
type SoaPluralKey = (typeof PLURAL_KEYS)[number];

const APPLICABILITY_TONE: Record<SoaApplicability, Tone> = {
  APPLICABLE: 'good',
  EXCLUDED: 'neutral',
  // Not neutral: an undecided control is outstanding work, and the colour has
  // to say so or the screen looks finished when it is not.
  UNDECIDED: 'serious',
};

const IMPLEMENTATION_TONE: Record<SoaImplementationStatus, Tone> = {
  IMPLEMENTED: 'good',
  PARTIALLY_IMPLEMENTED: 'warning',
  PLANNED: 'warning',
  NOT_IMPLEMENTED: 'critical',
};

// ---------------------------------------------------------------------------

function useSoaStrings() {
  const { t, i18n } = useTranslation();
  const tx = (key: SoaKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: SOA_STRINGS[key], ...options });
  const txn = (key: SoaPluralKey, count: number) =>
    t(key, {
      count,
      defaultValue: SOA_STRINGS[(count === 1 ? `${key}_one` : `${key}_other`) as SoaKey],
    });
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));
  return { tx, txn, formatDate };
}

// ===========================================================================

export function StatementOfApplicabilityPage() {
  const { tx, txn, formatDate } = useSoaStrings();
  const describeError = useApiError();
  const queryClient = useQueryClient();

  const [theme, setTheme] = useState('');
  const [applicability, setApplicability] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkJustification, setBulkJustification] = useState('');
  const [bulkReasons, setBulkReasons] = useState<Set<SoaJustificationReason>>(new Set());
  const [scopeDraft, setScopeDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['soa'],
    queryFn: () => api.get<SoaDocument>('/soa'),
  });

  /**
   * Both write endpoints return the whole document, so the cache is replaced
   * rather than invalidated. With 93 rows and a bulk action that can touch all
   * of them, a refetch per keystroke-sized edit would make the screen feel
   * worse the more work you get done.
   */
  const applyResult = (next: SoaDocument) => {
    queryClient.setQueryData(['soa'], next);
    setSaveError(null);
  };

  const patchOne = useMutation({
    mutationFn: ({ controlId, patch }: { controlId: string; patch: DecisionPatch }) =>
      api.patch<SoaDocument>(`/soa/controls/${encodeURIComponent(controlId)}`, patch),
    onSuccess: applyResult,
    onError: (error) => setSaveError(describeError(error)),
  });

  const patchMany = useMutation({
    mutationFn: ({ controlIds, decision }: { controlIds: string[]; decision: DecisionPatch }) =>
      api.post<SoaDocument>('/soa/bulk', { controlIds, decision }),
    onSuccess: applyResult,
    onError: (error) => setSaveError(describeError(error)),
  });

  const saveScope = useMutation({
    mutationFn: (ismsScope: string | null) =>
      api.patch<{ ismsScope: string | null }>('/soa/scope', { ismsScope }),
    onSuccess: (result) => {
      queryClient.setQueryData<SoaDocument>(['soa'], (current) =>
        current
          ? { ...current, organization: { ...current.organization, ismsScope: result.ismsScope } }
          : current,
      );
      setScopeDraft(null);
      setSaveError(null);
    },
    onError: (error) => setSaveError(describeError(error)),
  });

  const shown = useMemo(() => {
    const rows = data?.rows ?? [];
    const needle = search.trim().toLowerCase();
    return rows
      .filter((r) => (theme ? r.theme === theme : true))
      .filter((r) => (applicability ? r.applicability === applicability : true))
      .filter((r) =>
        needle
          ? r.code.toLowerCase().includes(needle) || r.title.toLowerCase().includes(needle)
          : true,
      );
  }, [data?.rows, theme, applicability, search]);

  if (isLoading) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-80" />
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="oc-page">
        <ErrorNotice message={tx('soa.loadFailed')} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (data.rows.length === 0) {
    return (
      <div className="oc-page">
        <EmptyState title={tx('soa.empty')} description={tx('soa.emptyHint')} />
      </div>
    );
  }

  const { summary } = data;
  const undecidedJustifications =
    summary.applicableWithoutJustification + summary.excludedWithoutJustification;

  const toggleSelected = (controlId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(controlId)) next.delete(controlId);
      else next.add(controlId);
      return next;
    });
  };

  const setRowApplicability = (row: SoaRow, next: SoaApplicability) => {
    if (row.applicability === next) return;
    patchOne.mutate({ controlId: row.controlId, patch: { applicability: next } });
  };

  /** Arrow keys move; A / E / U decide; Space selects. */
  const onRowKeyDown = (event: KeyboardEvent<HTMLLIElement>, row: SoaRow, index: number) => {
    // Never steal a key from a field the user is typing in.
    const target = event.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

    const focusRow = (offset: number) => {
      const next = shown[index + offset];
      if (!next) return;
      event.preventDefault();
      rowRefs.current[next.controlId]?.focus();
    };

    switch (event.key) {
      case 'ArrowDown': focusRow(1); break;
      case 'ArrowUp': focusRow(-1); break;
      case 'a': case 'A': event.preventDefault(); setRowApplicability(row, 'APPLICABLE'); break;
      // No keyboard shortcut for exclusion. It needs a reason, and a reason is
      // not something a single keypress can supply.

      case 'u': case 'U': event.preventDefault(); setRowApplicability(row, 'UNDECIDED'); break;
      case ' ': event.preventDefault(); toggleSelected(row.controlId); break;
      default: break;
    }
  };

  const runBulk = (decision: DecisionPatch) => {
    if (selected.size === 0) return;
    patchMany.mutate({ controlIds: [...selected], decision });
  };

  const scopeValue = scopeDraft ?? data.organization.ismsScope ?? '';
  const busy = patchOne.isPending || patchMany.isPending;

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('soa.title')}</h1>
        <p className="oc-muted mt-1 max-w-3xl">{tx('soa.subtitle')}</p>
        <p className="oc-muted mt-1 text-xs">
          {data.revisedAt
            ? tx('soa.revisedAt', { date: formatDate(data.revisedAt) })
            : tx('soa.neverRevised')}
        </p>
      </header>

      {saveError && <ErrorNotice message={saveError} />}

      {/* --- Scope ------------------------------------------------------- */}
      <Card>
        <Field label={tx('soa.scope')} hint={tx('soa.scopeHint')} htmlFor="soa-scope">
          <textarea
            id="soa-scope"
            className="oc-input min-h-24"
            value={scopeValue}
            placeholder={tx('soa.scopePlaceholder')}
            onChange={(e) => setScopeDraft(e.target.value)}
          />
        </Field>
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            disabled={scopeDraft === null || saveScope.isPending}
            onClick={() => saveScope.mutate(scopeDraft?.trim() ? scopeDraft.trim() : null)}
          >
            {tx('soa.scopeSave')}
          </Button>
          {!data.organization.ismsScope && (
            <p className="text-xs text-serious">{tx('soa.scopeMissing')}</p>
          )}
        </div>
      </Card>

      {/* --- Where the document stands ----------------------------------- */}
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SoaStat label={tx('soa.applicable')} value={summary.applicable} tone="good" />
          <SoaStat label={tx('soa.excluded')} value={summary.excluded} tone="neutral" />
          <SoaStat
            label={tx('soa.undecided')}
            value={summary.undecided}
            tone={summary.undecided > 0 ? 'critical' : 'good'}
          />
          <SoaStat label={tx('soa.implemented')} value={summary.implemented} tone="good" />
        </div>

        {summary.undecided > 0 && (
          <p className="mt-4 text-sm text-critical">
            {txn('soa.undecidedWarning', summary.undecided)}
          </p>
        )}
        {undecidedJustifications > 0 && (
          <p className="mt-2 text-sm text-serious">
            {txn('soa.noJustification', undecidedJustifications)}
          </p>
        )}
      </Card>

      {/* --- Filters ----------------------------------------------------- */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="oc-label">{tx('soa.filterTheme')}</span>
          <select className="oc-input" value={theme} onChange={(e) => setTheme(e.target.value)}>
            <option value="">{tx('soa.filterAllThemes')}</option>
            {data.themes.map((t) => (
              <option key={t.code} value={t.code}>{`${t.code} — ${t.name} (${t.count})`}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="oc-label">{tx('soa.filterApplicability')}</span>
          <select
            className="oc-input"
            value={applicability}
            onChange={(e) => setApplicability(e.target.value)}
          >
            <option value="">{tx('soa.filterAllDecisions')}</option>
            {SOA_APPLICABILITY.map((value) => (
              <option key={value} value={value}>{tx(`soa.applicability.${value}`)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 min-w-48">
          <span className="oc-label">{tx('soa.search')}</span>
          <input
            className="oc-input"
            value={search}
            placeholder={tx('soa.searchPlaceholder')}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <Button size="sm" onClick={() => setApplicability('UNDECIDED')}>
          {tx('soa.undecidedOnly')}
        </Button>
        <p className="oc-muted text-xs pb-2">
          {tx('soa.showing', { shown: shown.length, total: data.rows.length })}
        </p>
      </div>

      <p className="oc-muted text-xs">{tx('soa.keyboardHint')}</p>

      {/* --- Bulk bar ----------------------------------------------------- */}
      <Card className="sticky top-2 z-10">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => setSelected(new Set(shown.map((r) => r.controlId)))}
          >
            {tx('soa.selectAll')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            {tx('soa.clearSelection')}
          </Button>
          <span className="text-xs text-ink-muted">{txn('soa.selected', selected.size)}</span>
        </div>

        {selected.size > 0 && (
          <div className="mt-3 space-y-3 border-t border-line pt-3">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() =>
                  runBulk({
                    applicability: 'APPLICABLE',
                    ...(bulkJustification.trim() ? { justification: bulkJustification.trim() } : {}),
                    ...(bulkReasons.size ? { reasons: [...bulkReasons] } : {}),
                  })
                }
              >
                {tx('soa.bulkApplicable')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => runBulk({ applicability: 'UNDECIDED' })}
              >
                {tx('soa.bulkUndecided')}
              </Button>
            </div>

            {/* There is no bulk "exclude" and there must never be one. The
                server refuses it outright (SOA_BULK_EXCLUSION_REFUSED); this
                note is here so the absence reads as a decision rather than an
                oversight to the next person who edits this file. */}
            <p className="text-xs text-ink-muted">{tx('soa.bulkExcludeRefused')}</p>

            <div>
              <span className="oc-label">{tx('soa.bulkReasons')}</span>
              <ReasonChips
                selected={bulkReasons}
                onToggle={(reason) =>
                  setBulkReasons((current) => {
                    const next = new Set(current);
                    if (next.has(reason)) next.delete(reason);
                    else next.add(reason);
                    return next;
                  })
                }
              />
            </div>

            <label className="block">
              <span className="oc-label">{tx('soa.bulkJustification')}</span>
              <textarea
                className="oc-input min-h-16"
                value={bulkJustification}
                onChange={(e) => setBulkJustification(e.target.value)}
              />
            </label>
          </div>
        )}
      </Card>

      {/* --- The register -------------------------------------------------- */}
      <ul className="space-y-3">
        {shown.map((row, index) => (
          <li
            key={row.controlId}
            tabIndex={0}
            ref={(element) => { rowRefs.current[row.controlId] = element; }}
            onKeyDown={(event) => onRowKeyDown(event, row, index)}
            className="rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <SoaRowCard
              row={row}
              selected={selected.has(row.controlId)}
              busy={busy}
              onToggleSelected={() => toggleSelected(row.controlId)}
              onPatch={(patch) => patchOne.mutate({ controlId: row.controlId, patch })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SoaStat({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  const colour: Record<Tone, string> = {
    good: 'text-good',
    warning: 'text-warning',
    serious: 'text-serious',
    critical: 'text-critical',
    neutral: 'text-ink',
  };
  return (
    <div>
      <p className="oc-label">{label}</p>
      <p className={cn('text-2xl font-semibold tabular-nums', colour[tone])}>{value}</p>
    </div>
  );
}

function ReasonChips({
  selected,
  onToggle,
}: {
  selected: Set<SoaJustificationReason>;
  onToggle: (reason: SoaJustificationReason) => void;
}) {
  const { tx } = useSoaStrings();
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {SOA_JUSTIFICATION_REASONS.map((reason) => {
        const active = selected.has(reason);
        return (
          <button
            key={reason}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(reason)}
            className={cn(
              'px-2.5 py-1.5 rounded-full text-xs border transition-colors',
              active
                ? 'bg-brand text-white border-brand'
                : 'border-line text-ink-secondary hover:border-brand',
            )}
          >
            {tx(`soa.reason.${reason}`)}
          </button>
        );
      })}
    </div>
  );
}

function SoaRowCard({
  row,
  selected,
  busy,
  onToggleSelected,
  onPatch,
}: {
  row: SoaRow;
  selected: boolean;
  busy: boolean;
  onToggleSelected: () => void;
  onPatch: (patch: DecisionPatch) => void;
}) {
  const { tx, formatDate } = useSoaStrings();
  const [justification, setJustification] = useState(row.justification ?? '');
  const [dirty, setDirty] = useState(false);
  const [excluding, setExcluding] = useState(false);
  const [ground, setGround] = useState<SoaExclusionGround>('OUTSIDE_SCOPE_BOUNDARY');
  const [groundText, setGroundText] = useState('');

  // Keep the field in step with a bulk action that rewrote this row, without
  // discarding what the user is part-way through typing.
  const serverJustification = row.justification ?? '';
  const [lastSeen, setLastSeen] = useState(serverJustification);
  if (serverJustification !== lastSeen && !dirty) {
    setLastSeen(serverJustification);
    setJustification(serverJustification);
  }

  const commitJustification = () => {
    if (!dirty) return;
    setDirty(false);
    onPatch({ justification: justification.trim() ? justification.trim() : null });
  };

  const guidance = SOA_CONTROL_GUIDANCE[row.code];

  /*
    Two different jobs, previously done by one string.

    The HINT says what a justification has to rest on — a risk, a law, a
    contract — and is shown as its own line once a decision exists, because a
    placeholder vanishes the moment somebody starts typing and that is exactly
    when they want the rule.

    The EXAMPLE goes in the placeholder, which is where it belongs and nowhere
    else: grey, gone on the first keystroke, never a value. An applicable
    control gets the inclusion example, an excluded one the exclusion example,
    and an undecided one gets neither — offering a model justification for a
    decision nobody has taken yet invites writing the reasons before the
    decision, which is the wrong way round.
  */
  const justificationHint =
    row.applicability === 'APPLICABLE'
      ? tx('soa.justificationApplicable')
      : row.applicability === 'EXCLUDED'
        ? tx('soa.justificationExcluded')
        : null;

  const justificationPlaceholder =
    row.applicability === 'APPLICABLE'
      ? guidance?.exampleInclusion
        ? tx('soa.justificationExample', { example: guidance.exampleInclusion })
        : tx('soa.justificationApplicable')
      : row.applicability === 'EXCLUDED'
        ? guidance?.exampleExclusion
          ? tx('soa.justificationExample', { example: guidance.exampleExclusion })
          // No exclusion example exists for a control the product treats as
          // universal, and inventing one would be the product arguing a case it
          // does not believe. The shape, not a sentence to copy.
          : tx('soa.justificationExcludedShape')
        : tx('soa.justificationUndecided');

  return (
    <Card className={cn('p-4', selected && 'ring-2 ring-brand')}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0"
          checked={selected}
          onChange={onToggleSelected}
          aria-label={`${row.code} ${row.title}`}
        />

        <div className="min-w-0 flex-1 space-y-3">
          {/* --- Identity and decision ---------------------------------- */}
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">
                <span className="font-mono text-xs text-ink-muted mr-2">{row.code}</span>
                {row.title}
              </p>
              <p className="text-xs text-ink-muted mt-0.5">
                {row.decidedAt
                  ? row.decidedBy
                    ? tx('soa.decidedBy', { date: formatDate(row.decidedAt), name: row.decidedBy })
                    : tx('soa.decidedOn', { date: formatDate(row.decidedAt) })
                  : null}
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5" role="group">
              {SOA_APPLICABILITY.map((value) => {
                const active = row.applicability === value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={busy}
                    aria-pressed={active}
                    /*
                      EXCLUDED opens the reason picker rather than writing the
                      decision. The server refuses an exclusion with no ground
                      and no wording, so a button that patched straight through
                      would just produce an error; more to the point, the reason
                      is the part of an exclusion an auditor actually reads, and
                      it should be asked for at the moment the decision is taken
                      rather than left for later.
                    */
                    onClick={() =>
                      value === 'EXCLUDED'
                        ? setExcluding(true)
                        : onPatch({ applicability: value })
                    }
                    className={cn(
                      'px-2.5 py-1.5 rounded-md text-xs border transition-colors',
                      active && value === 'APPLICABLE' && 'bg-good text-white border-good',
                      active && value === 'EXCLUDED' && 'bg-ink text-white border-ink',
                      active && value === 'UNDECIDED' && 'bg-serious text-white border-serious',
                      !active && 'border-line text-ink-secondary hover:border-brand',
                    )}
                  >
                    {tx(`soa.applicability.${value}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* The control in the reader's own words. The title alone
              ("Threat intelligence") is a term of art; this line is what makes
              the row answerable by someone who has never read the standard. */}
          {guidance?.about && (
            <p className="text-xs text-ink-secondary">
              {tx('soa.about', { about: guidance.about })}
            </p>
          )}

          {/* A proposal, not a value. It is rendered as a thing to accept,
              never as a pre-selected state, which is the whole difference
              between a suggestion and a silent default. */}
          {row.suggestion && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-line p-2">
              <p className="text-xs text-ink-secondary min-w-0 flex-1">
                {tx('soa.suggestion', { basis: row.suggestion.basis })}
              </p>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => onPatch({ applicability: 'APPLICABLE' })}
              >
                {tx('soa.acceptSuggestion')}
              </Button>
            </div>
          )}

          {/* --- Exclusion reason ---------------------------------------- */}
          {excluding && (
            <div className="rounded-md border border-line p-3 space-y-2">
              <label className="block">
                <span className="oc-label">{tx('soa.excludeReason')}</span>
                <select
                  className="oc-input"
                  value={ground}
                  onChange={(e) => setGround(e.target.value as SoaExclusionGround)}
                >
                  {SOA_EXCLUSION_GROUNDS.map((value) => (
                    <option key={value} value={value}>{tx(`soa.ground.${value}`)}</option>
                  ))}
                </select>
              </label>
              {ground === 'OTHER' ? (
                <label className="block">
                  <span className="oc-label">{tx('soa.excludeReasonOther')}</span>
                  {/* This box IS the justification when the ground is OTHER,
                      so it gets the same example treatment — as a placeholder,
                      so an empty box still records nothing. */}
                  <textarea
                    className="oc-input min-h-16"
                    value={groundText}
                    placeholder={
                      guidance?.exampleExclusion
                        ? tx('soa.justificationExample', { example: guidance.exampleExclusion })
                        : tx('soa.justificationExcludedShape')
                    }
                    onChange={(e) => setGroundText(e.target.value)}
                  />
                </label>
              ) : (
                <p className="text-xs text-ink-muted italic">
                  {SOA_EXCLUSION_JUSTIFICATION[ground]}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || (ground === 'OTHER' && !groundText.trim())}
                  onClick={() => {
                    onPatch({
                      applicability: 'EXCLUDED',
                      exclusionGround: ground,
                      ...(ground === 'OTHER' ? { justification: groundText.trim() } : {}),
                    });
                    setExcluding(false);
                  }}
                >
                  {tx('soa.excludeConfirm')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setExcluding(false)}>
                  {tx('soa.excludeCancel')}
                </Button>
              </div>
            </div>
          )}

          {/* --- Justification ------------------------------------------ */}
          <div>
            <span className="oc-label">{tx('soa.justification')}</span>
            {justificationHint && (
              <p className="mb-1 text-xs text-ink-muted">{justificationHint}</p>
            )}
            <textarea
              className="oc-input min-h-16"
              value={justification}
              placeholder={justificationPlaceholder}
              onChange={(e) => { setJustification(e.target.value); setDirty(true); }}
              onBlur={commitJustification}
            />
            {row.applicability !== 'UNDECIDED' && !row.justification?.trim() && (
              <p className="mt-1 text-xs text-serious">{tx('soa.justificationMissing')}</p>
            )}
          </div>

          {/* --- Reasons ------------------------------------------------ */}
          {row.applicability !== 'UNDECIDED' && (
            <div>
              <span className="oc-label">{tx('soa.reasons')}</span>
              <ReasonChips
                selected={new Set(row.reasons)}
                onToggle={(reason) =>
                  onPatch({
                    reasons: row.reasons.includes(reason)
                      ? row.reasons.filter((r) => r !== reason)
                      : [...row.reasons, reason],
                  })
                }
              />
            </div>
          )}

          {/* --- Implementation ----------------------------------------- */}
          <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
            <label className="flex flex-col gap-1">
              <span className="oc-label">{tx('soa.implementation')}</span>
              <select
                className="oc-input"
                disabled={busy}
                /*
                  The select shows ONLY what a person stated. When nothing was
                  stated it sits on the empty option and the derived value is
                  rendered beside it as a labelled pill — never selected into
                  the control, because a selected option looks like a choice
                  somebody made.
                */
                value={row.implementationOrigin === 'STATED' ? row.implementationStatus ?? '' : ''}
                onChange={(e) =>
                  onPatch({
                    implementationStatus: e.target.value
                      ? (e.target.value as SoaImplementationStatus)
                      : null,
                  })
                }
              >
                <option value="">
                  {row.implementationOrigin === 'DERIVED'
                    ? tx('soa.implementationUseDerived')
                    : tx('soa.implementationNotStated')}
                </option>
                {SOA_IMPLEMENTATION_STATUSES.map((value) => (
                  <option key={value} value={value}>{tx(`soa.implementation.${value}`)}</option>
                ))}
              </select>
            </label>

            {row.implementationOrigin === 'DERIVED' && row.implementationStatus && (
              <div className="min-w-0 max-w-xl">
                <div className="flex items-center gap-2">
                  <Pill tone={IMPLEMENTATION_TONE[row.implementationStatus]}>
                    {tx(`soa.implementation.${row.implementationStatus}`)}
                  </Pill>
                  <Pill tone="neutral">{tx('soa.derived')}</Pill>
                </div>
                <p className="mt-1 text-xs text-ink-muted">{row.derivedFrom?.detail}</p>
                <p className="mt-0.5 text-xs text-ink-muted">{tx('soa.derivedHint')}</p>
              </div>
            )}

            <div className="min-w-0">
              <span className="oc-label">{tx('soa.evidence')}</span>
              <p className="text-xs text-ink-secondary mt-1">
                {row.evidence.length === 0
                  ? tx('soa.noEvidence')
                  : row.evidence
                      .map((e) => `${e.reference} (${e.evidenceCount})`)
                      .join(' · ')}
              </p>
            </div>
          </div>

          <p className="sr-only">
            <Pill tone={APPLICABILITY_TONE[row.applicability]}>
              {tx(`soa.applicability.${row.applicability}`)}
            </Pill>
          </p>
        </div>
      </div>
    </Card>
  );
}
